import React, { useState, forwardRef, useEffect } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";

import styles from "./NoContextItems.module.scss";
import { useCurrentOrganization } from "@/Hooks";
import { useSelector } from "react-redux";
import ContentLoader from "react-content-loader";
import { AnimatePresence } from "framer-motion";
import PhoneHomePageHorisontalNew from "@/Views/Virtual/PhoneHomePageHorisontalNew";
import { motion } from "framer-motion";
import Modal from "react-modal";
import { getFilterAppsData } from "@/entities/filterAppsData/model/selectors/getFilterAppsData";
import {
  useSelectedScreens,
  useSelectedSearchScreens,
} from "@/entities/selectedScreens";
import { SIMILAR_SCREEN_STORAGE_KEY } from "@/Views/SimilarScreen/SimilarScreenModal/SimilarScreenModal";

const NoContextItems = forwardRef(
  (
    {
      index,
      app,
      setSearchResults,
      onAppClick,
      handleExpandClick,
      handleOpenBillingPage,
      setSearchValue,
      handleContextMenu,
      transitionDirection,
      searchResults,
      currentAppIndex,
      setCurrentAppIndex,
      onNextApp,
      onPreviousApp,
      appDataToShow,
      appIndex,
      currentScreenNotBlurredID,
      setCurrentScreenNotBlurredID,
    },
    ref,
  ) => {
    const currentOrganization = useCurrentOrganization();
    const navigate = useNavigate();

    const { isScreensLoadingHome, isScreensLoadingStore } =
      useSelector(getFilterAppsData);

    //console.log(isScreensLoadingHome,isScreensLoadingStore,'1231231');

    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const [isHovered, setIsHovered] = useState(false);
    const [isHovered2, setIsHovered2] = useState(false);
    const { debugMode } = useOutletContext();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const location = useLocation();
    const isSearchPath = location.pathname.includes("/search");

    // Get both hooks
    const searchScreens = useSelectedSearchScreens();
    const appReplayScreens = useSelectedScreens();

    // Use the appropriate hook based on path
    const {
      addScreen,
      removeScreen,
      isSelected: checkIsSelected,
    } = isSearchPath ? searchScreens : appReplayScreens;

    const [isSelected, setIsSelected] = useState(false);

    const handleMouseMove = (e: React.MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    const modalVariants = {
      hidden: { opacity: 0, scale: 0.5, x: "-50%", y: "-50%" },
      visible: { opacity: 1, scale: 1, x: "-50%", y: "-50%" },
      exit: { opacity: 0, scale: 0.5, x: "-50%", y: "-50%" },
    };

    const contentVariants = {
      hidden: (direction: "next" | "prev") => ({
        opacity: 0,
        y: direction === "next" ? 100 : -100,
      }),
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.5,
          ease: "easeOut",
        },
      },
      exit: (direction: "next" | "prev") => ({
        opacity: 0,
        y: direction === "next" ? -100 : 100,
        transition: {
          duration: 0.5,
          ease: "easeIn",
        },
      }),
    };

    const handleClickPro = () => {
      handleOpenBillingPage();
    };

    const handleOpenModal = () => {
      setCurrentAppIndex(appIndex);
      setCurrentScreenNotBlurredID(app.id);
      setIsModalOpen(!isModalOpen);
    };

    const handlePreviousApp = () => {
      setCurrentScreenNotBlurredID(searchResults[currentAppIndex - 1].id);
      onPreviousApp(currentAppIndex);
    };

    const handleNextApp = () => {
      setCurrentScreenNotBlurredID(searchResults[currentAppIndex + 1].id);
      onNextApp(currentAppIndex);
    };

    // Handle selection for the main image view
    const handleSelectImage = (e: React.MouseEvent) => {
      e.stopPropagation();

      if (checkIsSelected(app.id)) {
        removeScreen(app.id);
        setIsSelected(false);
      } else {
        if (isSearchPath) {
          // For search screens
          addScreen({
            id: app.id,
            timestamp: app.timestamp,
            is_saved: app.is_saved,
            is_saved_pk: app.is_saved_pk,
            app_video_id: app.app_video_id,
          });
        } else {
          // For regular screens
          addScreen({
            id: app.id,
            ts: app.timestamp,
            is_saved_pk: app.is_saved_pk,
            app_video: app.app_video,
            isSaved: app.is_saved,
          });
        }
        setIsSelected(true);
      }
    };

    // Update selection state when selectedScreens changes
    useEffect(() => {
      setIsSelected(checkIsSelected(app.id));
    }, [app.id, checkIsSelected]);

    const handleSimilarClick = (e: React.MouseEvent) => {
      e.stopPropagation();

      // Determine which screen data to use (modal or main view)
      const screenToUse = app;

      const similarScreenData = {
        id: screenToUse.id,
        screen: screenToUse.screen,
        appId: screenToUse.app_video_id,
        timestamp: screenToUse.timestamp,
        appIcon: screenToUse.app.icon,
        appName: screenToUse.app.name,
        appDescription: screenToUse.app.developer.name,
      };

      localStorage.setItem(
        SIMILAR_SCREEN_STORAGE_KEY,
        JSON.stringify(similarScreenData),
      );

      window.open(`/similar-screens/${screenToUse.id}`, "_blank");
    };

    return (
      <div ref={ref} key={`${app.app.id}-${app.id}`} className={styles.appItem}>
        <div
          className={styles.photoItemTitle}
          onClick={() => onAppClick(app.app, app.timestamp, app.app_video_id)}
        >
          <img
            src={app.app.icon}
            alt={`${app.app.name} logo icon`}
            loading="lazy"
          />
          <div className={styles.photoItemContent}>
            <h3 className={styles.appName} title={app.app.name}>
              {app.app.name}
            </h3>
            <p className={styles.appShortname} title={app.app.developer.name}>
              {app.app.developer.name}
            </p>
          </div>
        </div>
        <div className={styles.photoContainer}>
          {isHovered && debugMode && (
            <div
              style={{
                width: "20%",
                position: "absolute",
                zIndex: 99,
                //left: 0,
                //top: 10,
                margin: "10px",
                color: "white",
                backgroundColor: "black",
                //transform: `translate(${cursorPosition.x}px, ${cursorPosition.y-20}px)`,
              }}
            >
              <strong>Score: {app.match_score}</strong>
              <br />
              <strong>Text: {app.match_text}</strong>
            </div>
          )}

          <div
            className={`${styles.containerPhoto}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onMouseMove={handleMouseMove}
          >
            {(isHovered || isSelected) && (
              <div className={styles.selectButtonContainer}>
                <div className={styles.selectButtonContainerInner}>
                  {isHovered && app.is_saved && (
                    <div className={styles.savedTag}>
                      <p>Saved</p>
                    </div>
                  )}

                  <button
                    className={`${styles.selectButton} ${isSelected ? styles.selected : ""}`}
                    onClick={handleSelectImage}
                  >
                    {isSelected && (
                      <img src="/img/blackIcon/Check.svg" alt="selectImage" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {!imageLoaded && (
              <div className={styles.skeletonWrapper}>
                <ContentLoader
                  speed={2}
                  width="100%"
                  height="650px"
                  viewBox="0 0 224 485"
                  backgroundColor="#f3f3f3"
                  foregroundColor="#ecebeb"
                  preserveAspectRatio="none"
                >
                  <rect x="0" y="0" rx="7" ry="7" width="100%" height="100%" />
                </ContentLoader>
              </div>
            )}

            <img
              src={app.screen}
              alt="screen"
              className={styles.photo}
              loading="lazy"
              style={{ visibility: imageLoaded ? "visible" : "hidden" }}
              onLoad={() => setImageLoaded(true)}
              onClick={() => handleOpenModal()}
              onContextMenu={(e) =>
                handleContextMenu(
                  e,
                  app.id.toString(),
                  app.timestamp,
                  app.app_video_id,
                )
              }
            />

            {app.is_blurry && (
              <div
                className={styles.unlockProOverlay}
                onClick={() =>
                  onAppClick(app.app, app.timestamp, app.app_video_id)
                }
              >
                <div
                  className={styles.unlockProContent}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div>
                    <h1>
                      <span>
                        <button
                          className={styles.Button}
                          onClick={(e) => {
                            e.preventDefault();
                            handleClickPro();
                          }}
                        >
                          Pro
                        </button>
                      </span>
                      Member's Only
                    </h1>
                  </div>
                  <div>
                    <button
                      className={styles.proButton}
                      onClick={(e) => {
                        e.preventDefault();
                        handleClickPro();
                      }}
                    >
                      Unlock Pro
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isHovered && !app.is_blurry && (
              <div className={styles.bottomMenuContainer}>
                <div className={styles.bottomMenuContainerInner}>
                  <button onClick={handleSimilarClick}>
                    <img src="/img/similar-icon.svg" alt="similar" />
                    <p>Similar</p>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAppClick(app.app, app.timestamp, app.app_video_id);
                    }}
                  >
                    <img src="/img/see-video-icon.svg" alt="see-video" />
                    <p>See Video</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {isModalOpen && (
            <Modal
              isOpen={isModalOpen}
              onRequestClose={handleOpenModal}
              contentLabel="Image Modal"
              className={styles.modal1}
              overlayClassName={styles.overlay}
            >
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{ position: "absolute", top: "50%", left: "50%" }}
              >
                <motion.div
                  key={currentAppIndex}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={contentVariants}
                  custom={transitionDirection}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <motion.div>
                    {currentAppIndex > 0 && !isScreensLoadingHome && (
                      <div className={styles.prevAppContainer}>
                        <button
                          className={styles.previusAppButton}
                          onClick={handlePreviousApp}
                          onMouseEnter={() => setIsHovered(true)}
                          onMouseLeave={() => setIsHovered(false)}
                        >
                          <img
                            src="/img/blackIcon/ArrowRight.svg"
                            alt="Arrow Right"
                          />
                        </button>
                        <p
                          className={`${styles.previusAppText} ${isHovered ? styles.visible : ""}`}
                        >
                          Go to previous Screen
                        </p>
                      </div>
                    )}

                    <AnimatePresence mode="wait" custom={transitionDirection}>
                      <motion.div
                        key={currentAppIndex}
                        custom={transitionDirection}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{ width: "100%" }}
                      >
                        <PhoneHomePageHorisontalNew
                          dataApi={
                            appIndex === currentAppIndex
                              ? app.app
                              : appDataToShow
                          }
                          app_video_id={app.app_video_id}
                          isOpen={false}
                          setIsPhonePageLoading={() => {}}
                          setIsSaveScreen={() => {}}
                          setLastClickTime={() => {}}
                          isFirstGroup={false}
                          currentOrganization={currentOrganization}
                          isPaywallOnly={false}
                          isPhonePageLoading={false}
                          ScreenNotBlurredID={currentScreenNotBlurredID}
                        />
                      </motion.div>
                    </AnimatePresence>

                    {searchResults.length > currentAppIndex + 1 &&
                      !isScreensLoadingHome && (
                        <div className={styles.nextAppContainer}>
                          <button
                            className={styles.nextAppButton}
                            onClick={handleNextApp}
                            onMouseEnter={() => setIsHovered2(true)}
                            onMouseLeave={() => setIsHovered2(false)}
                          >
                            <img
                              src="/img/blackIcon/ArrowRight.svg"
                              alt="Arrow Right"
                            />
                          </button>
                          <p
                            className={`${styles.nextAppText} ${isHovered2 ? styles.visible : ""}`}
                          >
                            Go to next Screen
                          </p>
                        </div>
                      )}
                  </motion.div>
                </motion.div>
              </motion.div>
            </Modal>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

NoContextItems.displayName = "NoContextItems";

export { NoContextItems };
